import React, { useCallback, useEffect, useRef } from 'react'
import { useInViewport } from 'react-in-viewport'
import { useDispatch, useSelector } from 'react-redux'
import {
  getVideosPagination,
  selectVideosState
} from 'modules/videos/ducks/selectors'
import {
  fetchVideosListWithInfiniteScrollRoutine,
  resetVideosListRoutine
} from 'modules/videos/ducks/actions'
import { useHistory } from 'react-router-dom'
import usePrevious from '../../../hooks/usePrevious'
import { getCurrentCourse } from 'modules/courses/ducks/selectors'
import { isNotNilOrEmpty } from 'utils/ramda'
import eventEmitter from 'providers/eventEmitter'
import events from '../utils/events'
import qs from 'qs'
import { pathOr } from 'ramda'
import { VIDEOS_QUERY_PARAMS } from 'utils/videos'
import { VIDEO_CATEGORIES } from '../utils/categories'
import { API_STATES } from 'utils/redux'

type VideoListInfiniteScrollerProps = {
  hideBonusVideos?: boolean
}

export const VideoListInfiniteScroller = ({
  hideBonusVideos = false
}: VideoListInfiniteScrollerProps): JSX.Element => {
  const containerRef = useRef(null)
  const lastFetchKeyRef = useRef('')
  // After a filter change / load-more, block the next page until the sentinel
  // leaves the viewport (user must scroll away, then to bottom again).
  const allowLoadMoreRef = useRef(false)
  // Sync lock so a second fetch cannot start before Redux flips to IN_PROGRESS.
  const isFetchingRef = useRef(false)
  const { inViewport } = useInViewport(containerRef, { rootMargin: '100px' })
  const pagination = useSelector(getVideosPagination)
  const videosState = useSelector(selectVideosState)
  const currentCourse = useSelector(getCurrentCourse)
  const hasCourseFetched = isNotNilOrEmpty(currentCourse?.id)
  const isLoading = videosState === API_STATES.IN_PROGRESS

  const dispatch = useDispatch()
  const {
    location: { search }
  } = useHistory()
  const prevSearch = usePrevious(search)
  const parsedQuery = qs.parse(search, { ignoreQueryPrefix: true })
  const selectedCategory = pathOr(
    'all',
    ['filter', VIDEOS_QUERY_PARAMS.category],
    parsedQuery
  )

  const page = Number(pagination.page)
  const pagesTotal = Number(pagination.pagesTotal)
  const isSearchChanging =
    prevSearch !== undefined && prevSearch !== search

  const fetchRecords = useCallback(
    query =>
      dispatch(
        fetchVideosListWithInfiniteScrollRoutine({ query, hideBonusVideos })
      ),
    [dispatch, hideBonusVideos]
  )

  const resetRecords = useCallback(
    () => dispatch(resetVideosListRoutine()),
    [dispatch]
  )

  // Drop cached list when leaving so reopening /videos starts from PRISTINE
  // and refetches instead of showing the previous visit's data.
  useEffect(() => {
    return () => {
      resetRecords()
    }
  }, [resetRecords])

  useEffect(() => {
    if (!isLoading) {
      isFetchingRef.current = false
    }
  }, [isLoading])

  useEffect(() => {
    const handleFavoriteToggle = () => {
      if (selectedCategory !== VIDEO_CATEGORIES.favourites) return
      lastFetchKeyRef.current = ''
      allowLoadMoreRef.current = false
      isFetchingRef.current = false
      resetRecords()
    }

    eventEmitter.on(events.favoriteToggle, handleFavoriteToggle)

    return () => {
      eventEmitter.off(events.favoriteToggle, handleFavoriteToggle)
    }
  }, [selectedCategory, resetRecords])

  useEffect(() => {
    if (!isSearchChanging) return

    lastFetchKeyRef.current = ''
    allowLoadMoreRef.current = false
    isFetchingRef.current = false
    resetRecords()
  }, [search, isSearchChanging, resetRecords])

  // Re-enable only after load finished AND the sentinel left the viewport.
  // Do not unlock while a request is in flight (layout shifts can briefly
  // hide the sentinel and would otherwise auto-chain the next page).
  useEffect(() => {
    if (isLoading) return
    if (!inViewport) {
      allowLoadMoreRef.current = true
    }
  }, [inViewport, isLoading])

  useEffect(() => {
    if (!hasCourseFetched) return
    if (isLoading || isFetchingRef.current) return

    // Tag/filter just changed: this render still has stale DONE + old pagination.
    // Wait for reset → PRISTINE before fetching (avoids a fake "load more" + page 1).
    if (isSearchChanging) return

    if (videosState === API_STATES.PRISTINE) {
      const fetchKey = `initial:${search}`
      if (lastFetchKeyRef.current === fetchKey) return

      lastFetchKeyRef.current = fetchKey
      isFetchingRef.current = true
      allowLoadMoreRef.current = false
      fetchRecords(search)
      return
    }

    const canLoadMore =
      videosState === API_STATES.DONE &&
      page < pagesTotal &&
      inViewport &&
      allowLoadMoreRef.current

    if (!canLoadMore) return

    const fetchKey = `more:${search}:${page}`
    if (lastFetchKeyRef.current === fetchKey) return

    lastFetchKeyRef.current = fetchKey
    isFetchingRef.current = true
    // Stay locked until this request finishes and the user scrolls the
    // sentinel out of view, then back to the bottom for the next hit.
    allowLoadMoreRef.current = false
    fetchRecords(search)
  }, [
    hasCourseFetched,
    videosState,
    isLoading,
    page,
    pagesTotal,
    inViewport,
    search,
    isSearchChanging,
    fetchRecords
  ])

  return (
    <div
      id='video-list-infinite-scroller'
      style={{ width: '1px', height: '1px', display: 'block' }}
      ref={containerRef}
    />
  )
}

export default VideoListInfiniteScroller
